import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { TrendingUp, Award } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Analytics = () => {
  const [evolution, setEvolution] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
    
    // Listen for company changes to refresh data
    const handleCompanyChange = () => {
      setLoading(true);
      fetchAnalytics();
    };
    
    window.addEventListener('companyChanged', handleCompanyChange);
    
    return () => {
      window.removeEventListener('companyChanged', handleCompanyChange);
    };
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [evolutionRes, rankingRes] = await Promise.all([
        axios.get(`${API_URL}/analytics/risk-evolution`),
        axios.get(`${API_URL}/analytics/sector-ranking`)
      ]);
      setEvolution(evolutionRes.data);
      setRanking(rankingRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div>Carregando...</div>;
  }

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
          Dashboard Analítico
        </h2>
        <p className="text-slate-600 dark:text-slate-400 mt-1">
          Análise avançada de tendências e indicadores
        </p>
      </div>

      {/* Evolution Chart */}
      <Card className="p-6" data-testid="evolution-chart">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-lg">
            <TrendingUp size={24} className="text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Evolução dos Riscos
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Tendência dos últimos 6 meses
            </p>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={evolution}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Line type="monotone" dataKey="total" stroke="#10B981" strokeWidth={2} name="Total" />
            <Line type="monotone" dataKey="alto" stroke="#EF4444" strokeWidth={2} name="Críticos" />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Sector Ranking */}
      <Card className="p-6" data-testid="sector-ranking">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-orange-100 dark:bg-orange-900 p-3 rounded-lg">
            <Award size={24} className="text-orange-600 dark:text-orange-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Ranking de Setores Críticos
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Setores ordenados por número de riscos críticos
            </p>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={ranking}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="setor" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="total" fill="#10B981" name="Total de Riscos" />
            <Bar dataKey="alto" fill="#EF4444" name="Riscos Críticos" />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Sector Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {ranking.map((sector, index) => (
          <Card key={sector.setor} className="p-6" data-testid={`sector-card-${sector.setor}`}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h4 className="font-semibold text-slate-900 dark:text-white">{sector.setor}</h4>
                <p className="text-xs text-slate-500 dark:text-slate-400">Ranking #{index + 1}</p>
              </div>
              {index === 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                  Atenção
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Total de Riscos</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{sector.total}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Críticos</p>
                <p className="text-2xl font-bold text-red-500">{sector.alto}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {ranking.length === 0 && (
        <Card className="p-12 text-center">
          <p className="text-slate-600 dark:text-slate-400">
            Dados insuficientes para gerar análises. Cadastre riscos para visualizar analytics.
          </p>
        </Card>
      )}
    </div>
  );
};

export default Analytics;
